import { NavigateNext } from '@mui/icons-material'
import { Box, Breadcrumbs, Divider, Fade, Link, Tab, Tabs, Theme, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { percent, px } from 'csx'
import { createContext, PropsWithChildren, ReactNode, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { UseAdministrationBreadcrumbsContext } from './AdministrationBreadcrumbsContext'

type AdministrationPageContextType = {
	actions?: ReactNode
}

const administrationPageContext = createContext<
	AdministrationPageContextType | undefined
>({})

export const AdministrationPageContextProvider = (
	props: PropsWithChildren<Record<string, unknown>>,
) => {
	return (
		<administrationPageContext.Provider value={{}}>
			{props.children}
		</administrationPageContext.Provider>
	)
}

type AdministrationPageContainerProps = {
	title: ReactNode
	actions?: ReactNode
	tabs?: string[]
	currentTab?: number
	divider?: boolean
	hideAttachmentsTab?: boolean
	onTabChanged?: ((tabIndex: number) => void)
} & PropsWithChildren<Record<string, unknown>>

type AdministrationPageProps = PropsWithChildren<Record<string, unknown>>

export const AdministrationPage = (props: AdministrationPageProps) => {
	const styles = useStyles()

	return <div className={styles.root}>{props.children}</div>
}

type AdministrationPageHeaderProps = PropsWithChildren<Record<string, unknown>>

export const AdministrationPageHeader = (
	props: AdministrationPageHeaderProps,
) => {
	const styles = useStyles()

	return <div className={styles.headerSection}> {props.children} </div>
}

type AdministrationPageHeaderTitleProps = PropsWithChildren<
	Record<string, unknown>
>

export const AdministrationPageTitle = (
	props: AdministrationPageHeaderTitleProps,
) => {
	const styles = useStyles()

	return (
		<div className={styles.headerLeft}>
			<Typography
				className={styles.title}
				variant="h5"
				component="h4"
				color="inherit"
			>
				{props.children}
			</Typography>
		</div>
	)
}

export const AdministrationPageBreadcrumbs = () => {
	const breadcrumbContext = UseAdministrationBreadcrumbsContext()
	const breadcrumbs = breadcrumbContext.breadcrumbs

	return (
		<Breadcrumbs separator={<NavigateNext />}>
			{breadcrumbs.map((breadcrumb, i) => {
				return (
					<Link
						variant="subtitle2"
						color="textSecondary"
						to={breadcrumb.link}
						key={i}
						component={RouterLink}
					>
						{breadcrumb.pageName}
					</Link>
				)
			})}
		</Breadcrumbs>
	)
}

export type AdministrationPageHeaderActionsProps = PropsWithChildren<
	Record<string, unknown>
>

export const AdministrationPageActions = (
	props: AdministrationPageHeaderActionsProps,
) => {
	const styles = useStyles()

	return <div className={styles.headerRight}>{props.children}</div>
}

type AdministrationPageContentProps = PropsWithChildren<Record<string, unknown>>

export const AdministrationPageContent = (
	props: AdministrationPageContentProps,
) => {
	const styles = useStyles()
	return <div className={styles.contentSection}>{props.children}</div>
}

const AdministrationPageContainer = (
	props: AdministrationPageContainerProps,
) => {
	const [selectedTabIndex, setSelectedTabIndex] = useState(props.currentTab ?? 0)
	const prevTabIndexRef = useRef(selectedTabIndex);

	const styles = useStyles();

	return (
		<AdministrationPage>
			<AdministrationPageHeader>
				<AdministrationPageTitle>
					{props.title}
					<AdministrationPageBreadcrumbs />
				</AdministrationPageTitle>
				<AdministrationPageActions>{props.actions}</AdministrationPageActions>
			</AdministrationPageHeader>
			<AdministrationPageContent>
				{props.tabs ?
					<Tabs
						classes={{ scrollButtons: styles.tabScrollButtons }}
						sx={{ position: 'relative' }}
						value={selectedTabIndex}
						variant="scrollable"
						scrollButtons="auto"
						indicatorColor="primary"
						textColor="primary"
						onChange={(e, v) =>
							setSelectedTabIndex((prev) => {
								if (prev !== v)
									props.onTabChanged && props.onTabChanged(v)
								return v
							})
						}
					>
						{props.tabs.map((tab: string, i) =>
							<Tab key={i} label={tab} hidden={tab === 'Attachment Types' && props.hideAttachmentsTab} />
						)}
					</Tabs>
					: null}
				{props.divider !== false && <Divider sx={{ marginBottom: 2 }} />}
				<Fade in>
					<Box className={styles.content}>
						{props.children}
					</Box>
				</Fade>
			</AdministrationPageContent>
		</AdministrationPage>
	)
}

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',

		marginTop: theme.spacing(3),

		width: percent(100),
		height: percent(100),
	},

	limitedWidthRoot: {
		[theme.breakpoints.up('lg')]: {
			maxWidth: px(1280),
		},
	},

	headerSection: {
		display: 'flex',

		width: percent(100),

		marginBottom: theme.spacing(2),
	},

	headerLeft: {
		display: 'flex',
		flexDirection: 'column',
	},

	headerRight: {
		display: 'flex',

		marginLeft: 'auto',
	},

	title: {
		fontWeight: 500,
	},

	contentSection: {
		display: 'flex',
		flex: '1',
		flexDirection: 'column',
		width: percent(100),
		maxHeight: `calc(100vh - ${theme.spacing(20)} - 1px)`,
	},

	content: {
		display: 'inline-block',
		flex: 1,
		overflow: 'auto',
		paddingBottom: theme.spacing(1),
	},

	tabScrollButtons: {
		position: 'absolute',
		zIndex: 1,
		height: '100%',
		backgroundColor: theme.palette.background.default,
		'&:last-child': {
			right: 0
		},
		'&:not(.Mui-disabled)': {
			opacity: .9
		}
	},
}))

export default AdministrationPageContainer
